import { useCallback, useEffect, useState } from "react";
import { object, Output, passthrough, safeParse } from "valibot";

import { debugLog } from "shared/functions";
import { useConfigContext, useGlobalsContext } from "shared/hooks";
import {
  ActionsEventSchema,
  ActionsSchema,
  ParentWindowScrollSchema,
  ParentWindowSizeSchema,
} from "shared/schemas";

import { ParsedEventData } from "./types";
import {
  getHandleMessage,
  getParsedDetail,
  processParentUrlEvent,
  reportSchemaIssues,
} from "./functions";

function useHandleEvents() {
  const { instanceName } = useConfigContext();
  const {
    setWidgetOpen,
    setWidgetExpanded,
    setCurrentUserFormData,
    setParentWindowSize,
    setParentWindowScroll,
  } = useGlobalsContext();

  const [actions, setActions] = useState<Output<typeof ActionsSchema>>([]);

  const handleEventMessage = useCallback(
    (eventData: ParsedEventData) => {
      switch (eventData.message) {
        case "show": {
          return setWidgetOpen(true);
        }

        case "hide": {
          return setWidgetOpen(false);
        }

        case "open": {
          return setWidgetExpanded(true);
        }

        case "close": {
          return setWidgetExpanded(false);
        }

        case "botsonic-event": {
          switch (eventData.type) {
            case "parent-url": {
              return processParentUrlEvent(eventData.detail);
            }

            case "current-user-form-data": {
              const result = safeParse(
                passthrough(object({})),
                eventData.detail,
              );
              if (result.success) {
                return setCurrentUserFormData(result.output);
              }
              return reportSchemaIssues(
                "Failed to parse user data",
                eventData.detail,
                result.issues,
              );
            }

            case "schedule-actions": {
              const result = safeParse(ActionsEventSchema, eventData.detail);
              if (result.success) {
                return setActions(result.output.actions);
              }
              return reportSchemaIssues(
                "Failed to schedule actions",
                eventData.detail,
                result.issues,
              );
            }

            case "parent-window-resize": {
              const result = safeParse(
                ParentWindowSizeSchema,
                eventData.detail,
              );
              if (result.success) {
                return setParentWindowSize(result.output);
              }
              return debugLog("Failed to process event", eventData);
            }

            case "parent-window-scroll": {
              const result = safeParse(
                ParentWindowScrollSchema,
                eventData.detail,
              );
              if (result.success) {
                return setParentWindowScroll(result.output.scrollPercentage);
              }
              return debugLog("Failed to process event", eventData);
            }

            // No default
          }

          return window?.dispatchEvent(
            new CustomEvent(String(eventData.type), {
              detail: getParsedDetail(eventData.detailType, eventData.detail),
            }),
          );
        }

        default: {
          debugLog("Message was not handled", eventData.message);
        }
      }
    },
    [
      setWidgetOpen,
      setWidgetExpanded,
      setCurrentUserFormData,
      setParentWindowSize,
      setParentWindowScroll,
    ],
  );

  useEffect(() => {
    const handleMessage = getHandleMessage(instanceName, handleEventMessage);
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [instanceName, handleEventMessage]);

  return {
    actions,
  };
}

export default useHandleEvents;
